import { aboutInfo } from '@/mocks/movies';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '51影院',
    url: 'https://www.51cinema.com',
    description: '51影院提供久久影院、悠久影院等全类型影视资源，电视剧大全、电影大全、综艺节目一应俱全，无需注册免费在线观看。',
    email: aboutInfo.contact.email,
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutInfo.contact.email,
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
      hoursAvailable: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          opens: '09:00',
          closes: '22:00',
        },
      ],
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutInfo.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-20">
        <Breadcrumb items={breadcrumbItems} />

        <section className="mb-10 md:mb-14">
          <div className="relative rounded-xl overflow-hidden h-[280px] md:h-[360px] mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20modern%20home%20entertainment%20space%20with%20large%20screen%20displaying%20warm%20abstract%20visuals%2C%20cozy%20ambient%20lighting%2C%20comfortable%20seating%2C%20minimalist%20design%2C%20warm%20golden%20and%20cream%20tones%2C%20inviting%20atmosphere%2C%20lifestyle%20photography&width=1920&height=600&seq=about-hero-51cinema&orientation=landscape"
              alt="关于51影院 - 免费在线影视平台"
              title="51影院 - 关于我们"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-950/70 to-background-950/30 flex items-center">
              <div className="px-6 md:px-10">
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-3">
                  关于<span className="text-primary-400">51影院</span>
                </h1>
                <p className="text-sm md:text-base text-background-200 max-w-lg leading-relaxed">
                  {aboutInfo.slogan}
                </p>
              </div>
            </div>
          </div>

          <div className="max-w-3xl">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">品牌介绍</h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">{aboutInfo.introduction}</p>
            <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.history}</p>
          </div>
        </section>

        <section className="mb-10 md:mb-14">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-6 text-center">核心优势</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {aboutInfo.values.map((v, i) => (
              <div key={i} className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-3">
                  <i className={`${['ri-stack-line', 'ri-4k-line', 'ri-shield-check-line', 'ri-flashlight-line'][i]} text-lg`}></i>
                </div>
                <h3 className="text-sm font-heading font-bold text-foreground-900 mb-2">{v.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-10 md:mb-14">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-6 text-center">用户口碑</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
            {aboutInfo.reputation.map((r, i) => (
              <div key={i} className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-medium text-foreground-900">{r.platform}</span>
                  <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-accent-500 text-background-50">{r.rating}</span>
                </div>
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <i key={j} className={`ri-star-fill text-xs ${j < Math.floor(r.rating) ? 'text-primary-400' : 'text-background-300'}`}></i>
                  ))}
                </div>
                <p className="text-xs text-foreground-600 leading-relaxed">"{r.comment}"</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-10 md:mb-14">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-6 text-center">常见问题</h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {aboutInfo.faq.map((item, i) => (
              <details key={i} className="group rounded-lg bg-background-100 border border-background-200/70 overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                  <span className="text-sm font-medium text-foreground-900 pr-4">{item.question}</span>
                  <i className="ri-arrow-down-s-line text-foreground-400 transition-transform duration-300 group-open:rotate-180 flex-shrink-0"></i>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </section>

        <section className="pb-10 md:pb-14">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-6 text-center">联系我们</h2>
          <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-500">{aboutInfo.contact.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                  <i className="ri-wechat-line text-lg"></i>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-foreground-900 mb-1">微信公众号</h3>
                  <p className="text-xs text-foreground-500">{aboutInfo.contact.wechat}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                  <i className="ri-qq-line text-lg"></i>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-foreground-900 mb-1">QQ客服</h3>
                  <p className="text-xs text-foreground-500">{aboutInfo.contact.qq}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-foreground-900 mb-1">工作时间</h3>
                  <p className="text-xs text-foreground-500">{aboutInfo.contact.workingHours}</p>
                </div>
              </div>
            </div>

            <div className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200/70">
              <h3 className="text-sm font-heading font-bold text-foreground-900 mb-4">发送消息</h3>
              <form
                id="about-contact-form"
                data-readdy-form
                action="https://readdy.ai/api/form/d9nj874it35ii3jdls20"
                method="POST"
                target="_self"
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
                  if (honeypot) {
                    alert('消息已发送，我们会尽快回复您！');
                    return;
                  }
                  const formData = new FormData(form);
                  fetch(form.action, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                  })
                    .then(async (res) => {
                      const text = await res.text();
                      let parsed;
                      try { parsed = JSON.parse(text); } catch { parsed = {}; }
                      return { ok: res.ok, parsed };
                    })
                    .then(({ ok, parsed }) => {
                      if (ok && parsed?.code === 'OK') {
                        alert('感谢您的留言！我们会尽快回复您。');
                        form.reset();
                      } else {
                        const msg = parsed?.meta?.message || parsed?.message || '发送失败，请稍后再试';
                        alert(msg);
                      }
                    })
                    .catch(() => alert('网络错误，请稍后再试'));
                }}
              >
                <input type="hidden" name="form_name" value="51影院 联系我们" />
                <div>
                  <label htmlFor="contact-name" className="block text-xs text-foreground-600 mb-1.5">姓名</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs text-foreground-600 mb-1.5">邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs text-foreground-600 mb-1.5">留言内容</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-3 py-2 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="anti-spam-field"
                />
                <button
                  type="submit"
                  className="w-full px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  发送消息
                </button>
              </form>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}